import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BookmarkPlus, Brush, Compass, Gem, Heart, Loader2, Sparkles, Swords } from "lucide-react";
import { api } from "../lib/api";
import { PageShell, PremiumNotice } from "../components/PageShell";
import { formatApiError, isPremiumError, useAuth } from "../context/AuthContext";

export const PERSONAS = {
  wealthy: { name: "The Wealthy Self", icon: Gem, color: "#FFD700" },
  family: { name: "The Family Self", icon: Heart, color: "#FF6AD5" },
  adventurous: { name: "The Adventurous Self", icon: Compass, color: "#00F0FF" },
  creative: { name: "The Creative Self", icon: Brush, color: "#B026FF" },
  wise: { name: "The Wise Self", icon: Sparkles, color: "#7CFFB2" },
};

export default function Council() {
  const { id } = useParams();
  const { user, checked } = useAuth();
  const [question, setQuestion] = useState("");
  const [asked, setAsked] = useState("");
  const [rounds, setRounds] = useState([]);
  const [busy, setBusy] = useState(null);
  const [focus, setFocus] = useState(null);
  const [follow, setFollow] = useState("");

  const call = async (body, label) => {
    setBusy(label);
    try {
      const { data } = await api.post(`/journey/${id}/council`, body);
      setRounds((r) => [...r, { label, responses: data.responses }]);
    } catch (e) {
      toast.error(isPremiumError(e) ? "Council of You is Premium." : formatApiError(e));
    } finally {
      setBusy(null);
    }
  };
  const ask = () => { if (!question.trim()) return; setAsked(question); setRounds([]); call({ question, mode: "ask" }, "Answers"); };
  const debate = () => call({ question: asked, mode: "debate", previous: rounds.flatMap((r) => r.responses) }, "Debate");
  const askOne = () => { if (!follow.trim()) return; call({ question: follow, persona: focus, mode: "ask" }, `${PERSONAS[focus].name}: ${follow}`); setFollow(""); };
  const save = async () => {
    try { await api.post(`/journey/${id}/journal`, { type: "council", title: `Council: ${asked}`, content: rounds }); toast.success("Council saved to your journal."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <PageShell back={`/journey/${id}`} label="Council of You" wide title={<>The <span className="text-glow-gold">Council</span> of You</>}
      subtitle="Five fictional versions of you, each with different priorities. Bring them one life question and let them answer, debate, and push back.">
      <div className="grid grid-cols-5 gap-2 md:gap-4 mb-8" data-testid="council-ring">
        {Object.entries(PERSONAS).map(([k, p], i) => {
          const Icon = p.icon;
          return (
            <motion.button key={k} onClick={() => setFocus(focus === k ? null : k)} data-testid={`persona-${k}`}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: focus && focus !== k ? 0.4 : 1, y: i % 2 ? 14 : 0 }} transition={{ delay: i * 0.08 }}
              className="glass rounded-2xl p-3 md:p-5 flex flex-col items-center text-center" style={{ borderColor: `${p.color}66`, boxShadow: focus === k ? `0 0 30px ${p.color}55` : "none" }}>
              <span className="w-10 h-10 md:w-14 md:h-14 rounded-full flex items-center justify-center mb-2" style={{ border: `1px solid ${p.color}`, color: p.color, boxShadow: `0 0 18px ${p.color}55` }}><Icon size={20} /></span>
              <span className="text-[10px] md:text-sm font-display">{p.name}</span>
            </motion.button>
          );
        })}
      </div>
      {checked && !user?.is_premium ? <PremiumNotice feature="Council of You" /> : (<>
        <div className="glass rounded-3xl p-5 flex flex-col md:flex-row gap-2 mb-6">
          <input value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="Present a life question to all five…" data-testid="council-question"
            className="flex-1 bg-white/[0.03] border border-white/10 rounded-full px-5 py-3 text-white placeholder-white/30 focus:outline-none" />
          <button onClick={ask} disabled={!!busy} className="btn-pill btn-pill-gold justify-center" data-testid="council-ask">{busy === "Answers" ? <Loader2 size={14} className="animate-spin" /> : null} Ask the Council</button>
        </div>
        {rounds.map((r, ri) => (
          <div key={ri} className="mb-6" data-testid={`council-round-${ri}`}>
            <div className="font-mono-label mb-3">{r.label}</div>
            <div className="grid md:grid-cols-2 gap-3">
              {r.responses.map((x, i) => {
                const p = PERSONAS[x.persona];
                return <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  className="glass rounded-2xl p-4 text-sm" style={{ borderColor: `${p.color}55` }}>
                  <div className="text-xs mb-1" style={{ color: p.color }}>{p.name}</div><p className="text-white/80 leading-relaxed">{x.text}</p></motion.div>;
              })}
            </div>
          </div>
        ))}
        {rounds.length > 0 && (
          <div className="flex flex-wrap gap-3 items-center">
            <button onClick={debate} disabled={!!busy} className="btn-pill btn-pill-primary" data-testid="council-debate">{busy === "Debate" ? <Loader2 size={14} className="animate-spin" /> : <Swords size={14} />} Let them debate</button>
            <button onClick={save} className="btn-pill" data-testid="council-save"><BookmarkPlus size={14} /> Save to journal</button>
            {focus && <div className="flex gap-2 flex-1 min-w-[260px]">
              <input value={follow} onChange={(e) => setFollow(e.target.value)} placeholder={`Ask ${PERSONAS[focus].name} directly…`} data-testid="council-follow"
                className="flex-1 bg-white/[0.03] border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/30 focus:outline-none" />
              <button onClick={askOne} disabled={!!busy} className="btn-pill text-xs" data-testid="council-follow-send">Ask</button></div>}
          </div>
        )}
        {!focus && rounds.length > 0 && <p className="text-xs text-white/40 mt-3">Tap one of the five selves above to question them individually.</p>}
      </>)}
    </PageShell>
  );
}
